import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { dashboardAPI, profileAPI, subscriptionAPI } from '../services/apiEndpoints';

// Loaded once on mount from /dashboard/stats — streak fields refresh with the profile

export const ContentVertexContext = createContext(null);

export const useContentVertex = () => {
    const ctx = useContext(ContentVertexContext);
    if (!ctx) throw new Error('useContentVertex must be used within ContentVertexProvider');
    return ctx;
};

export function ContentVertexProvider({ children, onOpenQuickPost }) {
    const [pendingCount, setPendingCount]               = useState(0);
    const [readyToPublishCount, setReadyToPublishCount] = useState(0);
    const [niche, setNiche]                             = useState(null);
    const [creatorTier, setCreatorTier]                 = useState('starter');
    const [onboardingComplete, setOnboardingComplete]   = useState(true);
    const [profileLoaded, setProfileLoaded]             = useState(false);
    const [currentStreak, setCurrentStreak]             = useState(0);
    const [longestStreak, setLongestStreak]             = useState(0);
    const [streakAtRisk, setStreakAtRisk]               = useState(false);
    const [creditBalance, setCreditBalance]             = useState(null);
    const [creditLimit, setCreditLimit]                 = useState(null);

    const refreshPendingCount = useCallback(async () => {
        try {
            const res = await dashboardAPI.getPendingCount();
            setPendingCount(res.data.pending_count ?? 0);
            setReadyToPublishCount(res.data.ready_to_publish_count ?? 0);
        } catch (e) {
            // non-critical
        }
    }, []);

    const refreshCredits = useCallback(async () => {
        try {
            const res = await subscriptionAPI.getUsage();
            const u = res.data.usage;
            setCreditBalance(u.credits_remaining ?? null);
            setCreditLimit(u.credits_limit ?? null);
        } catch (e) {
            // non-critical
        }
    }, []);

    const refreshProfile = useCallback(async () => {
        try {
            const [profileRes, statsRes, usageRes] = await Promise.all([
                profileAPI.get(),
                dashboardAPI.getStats(),
                subscriptionAPI.getUsage(),
            ]);
            const u = profileRes.data.user;
            setNiche(u.niche || null);
            setCreatorTier(u.creator_tier || 'starter');
            setOnboardingComplete(u.onboarding_complete ?? true);

            const s = statsRes.data.stats ?? {};
            setCurrentStreak(s.current_streak ?? 0);
            setLongestStreak(s.longest_streak ?? 0);
            setStreakAtRisk(s.streak_at_risk ?? false);

            const credits = usageRes.data.usage;
            setCreditBalance(credits.credits_remaining ?? null);
            setCreditLimit(credits.credits_limit ?? null);
        } catch (e) {
            // non-critical
        } finally {
            setProfileLoaded(true);
        }
    }, []);

    useEffect(() => {
        refreshPendingCount();
        const timer = setInterval(refreshPendingCount, 60_000);
        return () => clearInterval(timer);
    }, [refreshPendingCount]);

    useEffect(() => {
        refreshProfile();
    }, [refreshProfile]);

    return (
        <ContentVertexContext.Provider value={{
            pendingCount,
            readyToPublishCount,
            refreshPendingCount,
            niche,
            setNiche,
            creatorTier,
            onboardingComplete,
            setOnboardingComplete,
            profileLoaded,
            refreshProfile,
            currentStreak,
            longestStreak,
            streakAtRisk,
            creditBalance,
            creditLimit,
            refreshCredits,
            onGenerateClick: onOpenQuickPost,
        }}>
            {children}
        </ContentVertexContext.Provider>
    );
}
